/**
 * Minimal client for the rebelboost chat platform, speaking the same wire
 * protocol as @rebelmouseai/chat-sdk (runs via /codex/runs + SSE, client
 * tools answered through /ask-user). Port of reactdashboard's copilotChat.ts
 * without the chart and image-attachment support; collapses into the sdk
 * once it is published to the registry.
 *
 * Transport is same-origin: the rebelboost dashboard is reverse-proxied on
 * every runner under /agentic/platform, so /codex/runs and the SSE stream
 * resolve against the host the dashboard is loaded on — no cross-host URL,
 * no CORS.
 *
 * Identity: the signed-in CMS user's cross-auth JWT (session.js), which the
 * pages proxy accepts as a Bearer token and runs as the real user at this
 * site's property. Runs therefore carry the user and the site, so
 * platform-side tools such as the Client Backends MCP act as that user.
 */

import { buildEndpointContextBlock } from './editorSession.js';
import { clearStepOutputs, recordStepOutput } from './runOutputs.js';
import {
  clearCopilotSession,
  COPILOT_PLATFORM_BASE_PATH,
  getCopilotSession,
} from './session.js';

// The platform workflow this surface runs: the Client Backends copilot
// (rebelboost-auth seed/2026-09-29-clientbackends-copilot.sql). Build-time
// override through COPILOT_WORKFLOW_SLUG (preact.config.js), e.g. to fall
// back to the generic dashboard-copilot.
export const COPILOT_WORKFLOW_SLUG =
  process.env.COPILOT_WORKFLOW_SLUG || 'clientbackends-copilot';
export { COPILOT_PLATFORM_BASE_PATH };

export function buildContextBlock() {
  if (typeof window === 'undefined') {
    return '';
  }
  const { host, pathname, search } = window.location;
  const dashboard = [
    '[dashboard-context]',
    'surface: client-backends',
    // The CMS host this dashboard is served on. It can differ from the
    // platform's site domain (home.rebelmouse.com redirects /r/ paths to
    // www.rebelmouse.com), and the writer API only answers on this host.
    `host: ${host}`,
    `path: ${pathname}`,
    search ? `query: ${search}` : null,
    `title: ${document.title}`,
    '[/dashboard-context]',
  ]
    .filter(Boolean)
    .join('\n');
  // The open editor's unsaved state, so the code writer edits what the user
  // sees and a recorded turn replays without any tool call.
  const endpoint = buildEndpointContextBlock();
  return endpoint ? `${dashboard}\n\n${endpoint}` : dashboard;
}

let conversationId;

/** Forget the server-side conversation so the next run starts a new thread. */
export function resetConversation() {
  conversationId = undefined;
  clearStepOutputs();
}

/**
 * Thumbs up/down on a reply (Claude-Code style, mirrors reactdashboard's
 * submitCopilotFeedback): `POST {chatApiUrl}/codex/runs/:runId/feedback`.
 * A rating-only call is telemetry; a text-bearing one (thumbs-down follow-up)
 * also feeds the platform feedback intake. input: { runId, rating: 'up'|'down',
 * text?, context?: { userPrompt?, assistantText? } }.
 */
export async function submitCopilotFeedback(input) {
  const session = await getCopilotSession(COPILOT_WORKFLOW_SLUG);
  const base = session.chatApiUrl.replace(/\/$/, '');
  const res = await fetch(`${base}/codex/runs/${input.runId}/feedback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.token}`,
    },
    body: JSON.stringify({
      rating: input.rating,
      text: input.text || '',
      workflowSlug: COPILOT_WORKFLOW_SLUG,
      context: {
        surface: 'client-backends',
        path: typeof window === 'undefined' ? undefined : window.location.pathname,
        title: typeof document === 'undefined' ? undefined : document.title,
        ...(input.context || {}),
      },
    }),
  });
  if (!res.ok) {
    throw new Error(`Copilot feedback failed: ${res.status}`);
  }
}

/**
 * handlers: { onStatus, onAssistant(text, { runId }), onError, onDone,
 * onToolCall, onToolResult, onAskUser }. clientTools: { clientTools: [...], execute }.
 */
export async function startCopilotRun(prompt, handlers, clientTools) {
  const session = await getCopilotSession(COPILOT_WORKFLOW_SLUG);
  const base = session.chatApiUrl.replace(/\/$/, '');

  const res = await fetch(`${base}/codex/runs`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.token}`,
    },
    body: JSON.stringify({
      prompt: `${buildContextBlock()}\n\n${prompt}`,
      siteId: session.siteId,
      projectName: COPILOT_WORKFLOW_SLUG,
      workflow: COPILOT_WORKFLOW_SLUG,
      conversationId,
      ...(clientTools && clientTools.clientTools.length
        ? { clientTools: clientTools.clientTools }
        : {}),
    }),
  });
  if (!res.ok) {
    if (res.status === 401 || res.status === 403) {
      // The token was refused: drop it so the next turn mints a fresh one.
      clearCopilotSession(COPILOT_WORKFLOW_SLUG);
      throw new Error('Copilot session expired — please try again.');
    }
    throw new Error(`Copilot run failed to start: ${res.status}`);
  }
  const data = await res.json();
  if (data.conversationId) {
    conversationId = data.conversationId;
  }
  if (!data.runId) {
    throw new Error('Copilot run did not return a runId');
  }

  const seenReplies = new Set();
  const handledToolCalls = new Set();
  const es = new EventSource(
    `${base}/codex/runs/${data.runId}/events?auth_token=${encodeURIComponent(
      session.token
    )}`
  );

  function finish() {
    es.close();
    handlers.onDone();
  }

  function postAnswers(callId, body) {
    return fetch(`${base}/codex/runs/${data.runId}/ask-user/${callId}/answers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.token}`,
      },
      body: JSON.stringify(body),
    });
  }

  es.addEventListener('status', event => {
    try {
      const payload = JSON.parse(event.data);
      if (payload.message) {
        handlers.onStatus(payload.message);
      }
    } catch {
      /* ignore */
    }
  });

  es.addEventListener('agent_message', event => {
    try {
      const payload = JSON.parse(event.data);
      if (payload.message) {
        seenReplies.add(payload.message);
        handlers.onAssistant(payload.message, { runId: data.runId });
      }
    } catch {
      /* ignore */
    }
  });

  es.addEventListener('tool_call', event => {
    (async () => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.tool !== 'client_tool' && payload.tool !== 'ask_user') {
          return;
        }
        const callId = payload.answersId;
        const args = payload.args || {};
        if (!callId || handledToolCalls.has(callId)) {
          return;
        }
        handledToolCalls.add(callId);

        if (payload.tool === 'ask_user') {
          const questions = args.questions || [];
          if (handlers.onAskUser) {
            handlers.onAskUser({
              id: callId,
              questions,
              submit: answers => postAnswers(callId, { answers, questions }),
            });
          }
          return;
        }

        const name = args.name;
        if (!name) {
          return;
        }
        // Human-readable title for the activity row, like the rail: the client
        // tool's own description, falling back to its internal name.
        const descriptor =
          clientTools && clientTools.clientTools.find(candidate => candidate.name === name);
        const tool = {
          id: callId,
          name,
          description: (descriptor && descriptor.description) || name,
          input: args.input || {},
        };
        if (handlers.onToolCall) {
          handlers.onToolCall(tool);
        }

        let result;
        try {
          if (!clientTools) {
            throw new Error('Client tools are not available for this run');
          }
          const output = await clientTools.execute(name, tool.input);
          result =
            output === undefined
              ? 'ok'
              : typeof output === 'string'
                ? output
                : JSON.stringify(output);
          if (handlers.onToolResult) {
            handlers.onToolResult(tool, output === undefined ? 'ok' : output, false);
          }
        } catch (error) {
          result = `Error: ${error instanceof Error ? error.message : String(error)}`;
          if (handlers.onToolResult) {
            handlers.onToolResult(tool, result, true);
          }
        }

        await postAnswers(callId, { result });
      } catch {
        // The workflow will eventually time out if its callback cannot be acknowledged.
      }
    })();
  });

  // A step that opted in (config.publishOutput) hands its structured output
  // to the client; world methods take it by reference (runOutputs.js).
  es.addEventListener('step_output', event => {
    try {
      const payload = JSON.parse(event.data);
      if (payload.stepId && payload.output !== undefined) {
        recordStepOutput(data.runId, payload.stepId, payload.output);
        if (handlers.onStepOutput) {
          handlers.onStepOutput({ runId: data.runId, stepId: payload.stepId, output: payload.output });
        }
      }
    } catch {
      /* ignore */
    }
  });

  es.addEventListener('completed', event => {
    try {
      const payload = JSON.parse(event.data);
      const finalReply = payload.result && payload.result.response;
      if (finalReply && !seenReplies.has(finalReply)) {
        handlers.onAssistant(finalReply, { runId: data.runId });
      }
    } catch {
      /* ignore */
    }
    finish();
  });

  es.addEventListener('failed', event => {
    try {
      const payload = JSON.parse(event.data);
      handlers.onError(payload.message || 'The Copilot run failed.');
    } catch {
      handlers.onError('The Copilot run failed.');
    }
    finish();
  });

  es.addEventListener('run.ended', finish);
  es.onerror = () => {
    if (es.readyState === EventSource.CLOSED) {
      finish();
    }
  };

  return { cancel: () => es.close() };
}
